<template>
  <div class="rbl" :class="{ 'rbl--dark': isDarkSkin }">
    <div v-if="loading" class="rbl__skeleton">
      <b-skeleton v-for="n in 6" :key="n" height="40px" class="mb-50"/>
    </div>

    <div v-else-if="!rows.length" class="rbl__empty">
      <i class="fa-solid fa-chart-simple"></i>
      <span>{{ $t('salesDashboard.noData') }}</span>
    </div>

    <div v-else class="rbl__list">
      <button
          v-for="(it, i) in rows"
          :key="it.key || i"
          type="button"
          class="rbl-row"
          @click="$emit('select', { value: it.key, label: it.label })"
      >
        <span class="rbl-row__rank" :class="{ 'rbl-row__rank--flag': !!it.flag }" :style="rankStyle(it)">
          <template v-if="it.flag">{{ it.flag }}</template>
          <template v-else>{{ i + 1 }}</template>
        </span>

        <span class="rbl-row__main">
          <span class="rbl-row__top">
            <span class="rbl-row__label" :title="it.label">{{ it.label }}</span>
            <span class="rbl-row__value">
              {{ formatValue(it.value) }}
              <small class="rbl-row__pct">{{ pct(it) }}%</small>
            </span>
          </span>
          <span class="rbl-row__track">
            <span class="rbl-row__fill" :style="fillStyle(it)"></span>
          </span>
        </span>

        <i class="fa-solid fa-chevron-right rbl-row__chevron"></i>
      </button>
    </div>
  </div>
</template>

<script>
import mixinBase from '@/components/mixin/mixinBase';

export default {
  name: 'RankedBarList',
  mixins: [mixinBase],
  props: {
    items: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    isDarkSkin: { type: Boolean, default: false },
    currency: { type: Boolean, default: false },
    limit: { type: Number, default: 12 },
  },
  computed: {
    rows() {
      return (this.items || []).slice(0, this.limit);
    },
    maxValue() {
      return this.rows.reduce((m, it) => Math.max(m, Math.abs(it.value || 0)), 0) || 1;
    },
    total() {
      return (this.items || []).reduce((t, it) => t + Math.abs(it.value || 0), 0) || 0;
    },
  },
  methods: {
    formatValue(v) {
      if (this.currency) return this.convertToEURO(v);
      return Number(v || 0).toLocaleString();
    },
    pct(it) {
      if (!this.total) return '0';
      return (Math.abs(it.value || 0) / this.total * 100).toFixed(1);
    },
    barWidth(v) {
      const w = Math.round((Math.abs(v || 0) / this.maxValue) * 100);
      return Math.max(4, w) + '%';
    },
    fillStyle(it) {
      const c = it.color;
      return {
        width: this.barWidth(it.value),
        background: c ? `linear-gradient(90deg, ${c}, ${c})` : 'linear-gradient(90deg, #4da3ff, #007BFF)',
      };
    },
    rankStyle(it) {
      if (it.flag) return {};
      if (it.color) return { background: it.color, color: '#fff' };
      return {};
    },
  },
};
</script>

<style scoped lang="scss">
.rbl {
  &__list { display: flex; flex-direction: column; gap: 0.3rem; }
  &__empty {
    display: flex; flex-direction: column; align-items: center; gap: 0.5rem;
    padding: 2.5rem 1rem; color: #b9b9c3; font-size: 0.88rem;
    i { font-size: 1.6rem; }
  }
  &__skeleton { padding: 0.25rem 0; }
}

.rbl-row {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  width: 100%;
  text-align: left;
  border: none;
  background: transparent;
  padding: 0.5rem 0.55rem;
  border-radius: 12px;
  cursor: pointer;
  transition: background 0.18s ease, transform 0.18s ease;

  &:hover {
    background: rgba(0, 123, 255, 0.06);
    transform: translateX(3px);
    .rbl-row__chevron { opacity: 1; transform: translateX(0); }
  }

  &__rank {
    flex-shrink: 0;
    width: 30px; height: 30px;
    display: flex; align-items: center; justify-content: center;
    border-radius: 9px;
    font-size: 0.78rem; font-weight: 700;
    color: #6e6b7b; background: rgba(110, 117, 159, 0.1);
    &--flag { font-size: 1.15rem; background: transparent; }
  }

  &__main { flex: 1; min-width: 0; }
  &__top {
    display: flex; align-items: baseline; justify-content: space-between;
    gap: 0.5rem; margin-bottom: 0.32rem;
  }
  &__label {
    font-weight: 600; font-size: 0.85rem; color: #1f2a44;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  &__value { flex-shrink: 0; font-weight: 700; font-size: 0.85rem; color: #1f2a44; white-space: nowrap; }
  &__pct { margin-left: 0.35rem; font-weight: 600; font-size: 0.7rem; color: #b9b9c3; }

  &__track {
    display: block; height: 8px; border-radius: 999px;
    background: rgba(110, 117, 159, 0.1); overflow: hidden;
  }
  &__fill {
    display: block; height: 100%; border-radius: 999px;
    transition: width 0.55s cubic-bezier(0.16, 1, 0.3, 1);
  }

  &__chevron {
    flex-shrink: 0; color: #b9b9c3; font-size: 0.8rem;
    opacity: 0; transform: translateX(-4px);
    transition: opacity 0.18s ease, transform 0.18s ease;
  }
}

.rbl--dark {
  .rbl-row {
    &:hover { background: rgba(123, 175, 255, 0.1); }
    &__label, &__value { color: #d0d2d6; }
    &__rank { color: #8b93ab; background: rgba(255, 255, 255, 0.06); }
    &__track { background: rgba(255, 255, 255, 0.08); }
    &__pct { color: #7c869c; }
  }
}
</style>
